const BASE_URL = 'https://thenjcarpenter.com';

export interface SEOConfig {
  title: string;
  description: string;
  keywords: string;
  ogImage?: string;
  canonical?: string;
  schema?: object;
}

export const updateSEO = (config: SEOConfig) => {
  document.title = config.title;

  updateMetaTag('name', 'description', config.description);
  updateMetaTag('name', 'keywords', config.keywords);
  updateMetaTag('name', 'title', config.title);

  const canonicalUrl = config.canonical ? `${BASE_URL}${config.canonical}` : BASE_URL + '/';
  updateLinkTag('canonical', canonicalUrl);

  updateMetaTag('property', 'og:title', config.title);
  updateMetaTag('property', 'og:description', config.description);
  updateMetaTag('property', 'og:url', canonicalUrl);
  updateMetaTag('property', 'og:type', 'website');
  updateMetaTag('property', 'og:image', config.ogImage ? `${BASE_URL}${config.ogImage}` : `${BASE_URL}/optimized/nj4-without_slogan-final.webp`);

  updateMetaTag('name', 'twitter:title', config.title);
  updateMetaTag('name', 'twitter:description', config.description);
  updateMetaTag('name', 'twitter:url', canonicalUrl);
  updateMetaTag('name', 'twitter:image', config.ogImage ? `${BASE_URL}${config.ogImage}` : `${BASE_URL}/optimized/nj4-without_slogan-final.webp`);

  if (config.schema) {
    updatePageSchema(config.schema);
  }
};

const updateMetaTag = (attribute: string, attributeValue: string, content: string) => {
  let element = document.querySelector(`meta[${attribute}="${attributeValue}"]`) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, attributeValue);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const updateLinkTag = (rel: string, href: string) => {
  let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
};

const updatePageSchema = (schema: object) => {
  const existingPageSchema = document.querySelector('script[data-page-schema]');
  if (existingPageSchema) existingPageSchema.remove();
  const script = document.createElement('script');
  script.setAttribute('type', 'application/ld+json');
  script.setAttribute('data-page-schema', 'true');
  script.textContent = JSON.stringify(schema);
  document.head.appendChild(script);
};

const faqSchema = (faqs: { question: string; answer: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: { '@type': 'Answer', text: f.answer },
  })),
});

export const pageSEO: Record<string, SEOConfig> = {
  home: {
    title: 'South Jersey Deck Builder | NJ Carpenter LLC',
    description: 'TimberTech PRO Certified installer building custom decks for South Jersey coastal homes. New construction, replacement, resurfacing, and repair. Free on-site consultation.',
    keywords: 'custom decks South Jersey, TimberTech PRO certified installer, deck builder South Jersey, deck construction Ocean City, deck contractor Margate, Stone Harbor, Avalon, Cape May County, Egg Harbor Township',
    ogImage: '/optimized/nj4-without_slogan-final.webp',
    canonical: '/',
  },
  materials: {
    title: 'TimberTech Decking Materials — Composite & Advanced PVC | NJ Carpenter',
    description: 'Explore TimberTech composite and Advanced PVC decking options for South Jersey coastal homes. NJ Carpenter is a TimberTech PRO Certified installer serving the Jersey Shore.',
    keywords: 'TimberTech decking, composite decking, Advanced PVC decking, EDGE Prime+, Reserve Collection, coastal decking materials, Jersey Shore deck materials',
    ogImage: '/optimized/nj4-without_slogan-final.webp',
    canonical: '/#materials',
  },
  services: {
    title: 'Deck Construction & Repair — South Jersey | NJ Carpenter',
    description: 'Custom deck construction, replacement, resurfacing, and repair for South Jersey\'s coastal homeowners. TimberTech PRO Certified · Licensed & Insured · Serving Ocean City, Margate, Avalon & the Shore.',
    keywords: 'custom decks South Jersey, deck construction NJ, deck replacement South Jersey, deck resurfacing, deck repair Jersey Shore, TimberTech PRO certified installer, licensed insured NJ contractor',
    ogImage: '/optimized/nj4-without_slogan-final.webp',
    canonical: '/#services',
  },
  process: {
    title: 'Our Process — How NJ Carpenter Builds Decks | South Jersey',
    description: 'From consultation to completion — NJ Carpenter\'s proven process for custom deck construction. TimberTech PRO Certified · South Jersey\'s deck building specialist.',
    keywords: 'deck building process NJ, deck construction consultation, deck installation timeline, how we build decks, South Jersey contractor process',
    ogImage: '/optimized/nj4-without_slogan-final.webp',
    canonical: '/#process',
  },
  portfolio: {
    title: 'Project Portfolio — Custom Decks | NJ Carpenter',
    description: 'Browse NJ Carpenter\'s completed deck projects across South Jersey\'s shore communities. Real work, real results. TimberTech PRO Certified.',
    keywords: 'deck portfolio South Jersey, TimberTech deck installations, Jersey Shore deck photos, completed deck projects, custom deck gallery NJ',
    ogImage: '/optimized/nj4-without_slogan-final.webp',
    canonical: '/#portfolio',
  },
  about: {
    title: 'About NJ Carpenter — South Jersey\'s Deck Building Specialist',
    description: 'Meet Javier and the NJ Carpenter team. TimberTech PRO Certified deck builders serving Ocean City, Margate, Stone Harbor, Avalon, and the Jersey Shore for over 10 years.',
    keywords: 'about NJ Carpenter LLC, Javier carpenter South Jersey, TimberTech PRO certified contractor, deck builder NJ, licensed insured deck builder',
    ogImage: '/optimized/nj4-without_slogan-final.webp',
    canonical: '/#about',
  },
  faq: {
    title: 'FAQ — Custom Decks | NJ Carpenter South Jersey',
    description: 'Common questions about custom decks, TimberTech materials, and our build process. NJ Carpenter — South Jersey\'s deck building specialist.',
    keywords: 'deck FAQ NJ, deck building questions South Jersey, TimberTech questions, deck construction FAQ, deck repair FAQ',
    ogImage: '/optimized/nj4-without_slogan-final.webp',
    canonical: '/#faq',
    schema: faqSchema([
      { question: 'What areas of South Jersey do you serve?', answer: 'We serve all of South Jersey including Ocean City, Margate, Stone Harbor, Avalon, Sea Isle City, Wildwood, Cape May, Galloway, Egg Harbor Township, Little Egg Harbor, Brigantine, and surrounding areas in Cape May and Atlantic Counties.' },
      { question: 'Are you licensed and insured in New Jersey?', answer: 'Yes. NJ Carpenter LLC is fully licensed and insured in New Jersey. We carry general liability insurance and workers\' compensation coverage. Our license number is #13VH13299500.' },
      { question: 'What decking brands do you install?', answer: 'We are a TimberTech PRO Certified installer, specializing in TimberTech and AZEK composite and Advanced PVC decking products.' },
      { question: 'How long does a custom deck installation take?', answer: 'Most residential deck installations take between 3–7 days depending on size and complexity. Larger or more complex deck projects may take 1–2 weeks.' },
      { question: 'Do you offer free consultations?', answer: 'Yes. We offer free on-site consultations for all deck projects. Schedule yours online or by phone.' },
    ]),
  },
  'materials-faq': {
    title: 'Materials FAQ — TimberTech Decking Questions | NJ Carpenter',
    description: 'Frequently asked questions about TimberTech composite and Advanced PVC decking. Learn about durability, maintenance, warranties, and what makes TimberTech the best choice for coastal homes.',
    keywords: 'TimberTech FAQ, composite decking questions, Advanced PVC decking FAQ, deck materials warranty, TimberTech vs wood decking, coastal decking materials',
    ogImage: '/optimized/nj4-without_slogan-final.webp',
    canonical: '/#materials-faq',
    schema: faqSchema([
      { question: 'What is the difference between composite and Advanced PVC decking?', answer: 'Composite decking is made from a blend of wood fiber and plastic. Advanced PVC decking (like TimberTech AZEK) contains no wood fiber at all, making it fully waterproof, more resistant to mold, and the best choice for coastal environments.' },
      { question: 'How long does TimberTech decking last?', answer: 'TimberTech Advanced PVC decking products are backed by a 50-year limited warranty. Composite products carry a 30-year limited warranty. Both significantly outlast natural wood.' },
      { question: 'Does composite or PVC decking require sealing or staining?', answer: 'No. Unlike natural wood, TimberTech composite and PVC decking products require no sealing, staining, or painting. Regular cleaning with soap and water is all that\'s needed.' },
      { question: 'Is TimberTech decking safe near salt air and humidity?', answer: 'Yes. TimberTech Advanced PVC decking is specifically engineered for coastal environments. It resists moisture, salt air, mold, mildew, and UV fading — making it ideal for South Jersey shore homes.' },
    ]),
  },
  'services-faq': {
    title: 'Services FAQ — Deck Construction & Repair | NJ Carpenter',
    description: 'Common questions about our deck services — new construction, replacement, resurfacing, and repair. Get answers from South Jersey\'s deck building specialists.',
    keywords: 'deck services FAQ, deck construction questions, deck replacement FAQ NJ, deck resurfacing questions, deck repair South Jersey',
    ogImage: '/optimized/nj4-without_slogan-final.webp',
    canonical: '/#services-faq',
    schema: faqSchema([
      { question: 'What types of deck projects do you build?', answer: 'We specialize in custom composite and PVC deck construction, deck replacement, deck resurfacing, and deck repairs. We also install full railing systems — composite, PVC, metal, and cable — and build built-in seating and custom features.' },
      { question: 'Do you handle permits for deck construction in New Jersey?', answer: 'Yes. We handle the permit process for all deck projects requiring municipal permits. We are familiar with local requirements across South Jersey\'s shore municipalities.' },
      { question: 'Can you repair or replace an existing deck?', answer: 'Yes. We offer full deck repair services including board replacement, structural repairs, ledger board replacement, and full deck teardown and rebuilds. We also offer deck resurfacing — a new surface on your existing frame.' },
      { question: 'What railing systems do you install?', answer: 'We install the full range of railing systems: composite, PVC, metal, and cable railings. All are integrated with your deck design and installed to manufacturer specifications.' },
    ]),
  },
  'process-faq': {
    title: 'Process FAQ — Our Deck Building Process Questions | NJ Carpenter',
    description: 'Learn about how NJ Carpenter manages deck projects from first consultation through final walkthrough. Timelines, payments, permits, and what to expect at each stage.',
    keywords: 'deck project process FAQ, deck building timeline NJ, how long does deck take, NJ carpenter project management, consultation to completion process',
    ogImage: '/optimized/nj4-without_slogan-final.webp',
    canonical: '/#process-faq',
    schema: faqSchema([
      { question: 'What happens during the initial consultation?', answer: 'During the free consultation, we visit your property, discuss your vision, take measurements, review material options, and answer any questions. You\'ll receive a detailed written proposal within a few days.' },
      { question: 'How far in advance should I book?', answer: 'Our schedule typically books out 4–8 weeks during peak season (spring and summer). For fall and off-season projects, scheduling is usually faster. We recommend reaching out as early as possible.' },
      { question: 'Do you require a deposit to start a project?', answer: 'Yes. We require a deposit to reserve your build slot and order materials. The amount and payment schedule will be clearly outlined in your contract.' },
      { question: 'Will I need to be home during construction?', answer: 'You don\'t need to be home every day, but we recommend being available at the start to review plans on-site and at the end for a final walkthrough. We\'ll keep you updated throughout.' },
    ]),
  },
  contact: {
    title: 'Contact NJ Carpenter LLC — Get a Free Deck Consultation | South Jersey',
    description: 'Get in touch with NJ Carpenter LLC for your deck project. Request a free on-site consultation for custom deck construction, replacement, or repair. Serving the entire Jersey Shore.',
    keywords: 'contact NJ Carpenter, free deck consultation NJ, deck quote South Jersey, deck estimate Jersey Shore, call NJ Carpenter',
    ogImage: '/optimized/nj4-without_slogan-final.webp',
    canonical: '/#contact',
  },
  schedule: {
    title: 'Schedule a Consultation — Start Your Deck Project | NJ Carpenter LLC',
    description: 'Schedule your free on-site consultation with NJ Carpenter LLC. Tell us about your deck project and we\'ll help you design the perfect deck for your South Jersey home.',
    keywords: 'schedule deck consultation NJ, book free estimate South Jersey, deck consultation Jersey Shore, NJ Carpenter appointment',
    ogImage: '/optimized/nj4-without_slogan-final.webp',
    canonical: '/#schedule',
  },
  'thank-you': {
    title: 'Consultation Booked | NJ Carpenter LLC',
    description: 'Your free consultation with NJ Carpenter LLC is booked. Check your email for appointment details and next steps.',
    keywords: 'NJ Carpenter consultation confirmation, deck consultation appointment South Jersey',
    ogImage: '/optimized/nj4-without_slogan-final.webp',
    canonical: '/thank-you',
  },
  blog: {
    title: 'Blog — Straight Answers from a South Jersey Deck Builder | NJ Carpenter LLC',
    description: 'Real questions from real homeowners — answered honestly by Javier, owner of NJ Carpenter LLC. Covering decks, materials, costs, and deck building in New Jersey.',
    keywords: 'NJ deck builder blog, South Jersey deck building, deck materials New Jersey, NJ Carpenter blog, Javier Santiago deck tips',
    ogImage: '/optimized/nj4-without_slogan-final.webp',
    canonical: '/blog',
  },
  'service-area': {
    title: 'Service Area — South Jersey Deck Builder | NJ Carpenter LLC',
    description: 'NJ Carpenter LLC builds custom decks across South Jersey — Atlantic, Cape May, Ocean, Cumberland, Gloucester, Burlington, and Camden Counties. TimberTech PRO Certified · Licensed & Insured.',
    keywords: 'deck builder South Jersey, deck contractor service area, Atlantic County deck builder, Cape May County deck builder, Ocean County deck contractor, South Jersey deck installation',
    ogImage: '/optimized/nj4-without_slogan-final.webp',
    canonical: '/service-area',
  },
};
